<template>
  <HrPageLayout
    :title="pageTitle"
    subtitle="Recruitment verification"
    :breadcrumb-items="breadcrumbItems"
  >
    <div class="verification-page">
      <!-- Toast message -->
      <div v-if="message" class="verification-toast" @click="message = ''">
        <span :class="message.includes('success') ? 'verification-toast-success' : 'verification-toast-error'">
          {{ message }}
        </span>
        <button type="button" class="verification-toast-close" @click.stop="message = ''" aria-label="Close">×</button>
      </div>

      <div class="verification-card">
        <header class="verification-card-header">
          <h2 class="verification-card-title">Employee recruitment</h2>
          <router-link :to="{ name: 'employee-approvals-new' }" class="verification-back-link">
            <i class="bx bx-arrow-back"></i> Back to list
          </router-link>
        </header>

        <div class="verification-card-body">
          <section class="verification-section">
            <h3 class="verification-section-title">Personal Details</h3>
            <p v-if="submissionDetails.approved === 'false'" class="verification-edit-hint">You can correct these details before approving.</p>
            <template v-if="submissionDetails.approved === 'false'">
              <div class="verification-form-grid">
                <label>Title</label><input v-model="employee.title" type="text" class="verification-input" />
                <label>First Name</label><input v-model="employee.firstName" type="text" class="verification-input" />
                <label>Middle Name</label><input v-model="employee.middleName" type="text" class="verification-input" />
                <label>Last Name</label><input v-model="employee.lastName" type="text" class="verification-input" />
                <label>Date of Birth</label><input v-model="employee.dob" type="text" class="verification-input" placeholder="YYYY-MM-DD" />
                <label>Email <span class="text-danger">*</span></label><input v-model="employee.email" type="email" class="verification-input" />
                <label>Phone</label><input v-model="employee.phone" type="text" class="verification-input" />
                <label>National ID <span class="text-danger">*</span></label><input v-model="employee.nationalId" type="text" class="verification-input" />
                <label>Gender</label><input v-model="employee.gender" type="text" class="verification-input" />
                <label>Person with Disability</label><input v-model="employee.disabilityStatus" type="text" class="verification-input" />
                <label>Postal Code</label><input v-model="employee.postalCode" type="text" class="verification-input" />
                <label>Postal Town</label><input v-model="employee.postalTown" type="text" class="verification-input" />
                <label>Physical Address</label><input v-model="employee.physicalAddress" type="text" class="verification-input" />
              </div>
            </template>
            <dl v-else class="verification-dl">
              <dt>Title</dt><dd>{{ employee.title || '—' }}</dd>
              <dt>First Name</dt><dd>{{ employee.firstName || '—' }}</dd>
              <dt>Middle Name</dt><dd>{{ employee.middleName || '—' }}</dd>
              <dt>Last Name</dt><dd>{{ employee.lastName || '—' }}</dd>
              <dt>Date of Birth</dt><dd>{{ employee.dob || '—' }}</dd>
              <dt>Email</dt><dd>{{ employee.email || '—' }}</dd>
              <dt>Phone</dt><dd>{{ employee.phone || '—' }}</dd>
              <dt>National ID</dt><dd>{{ employee.nationalId || '—' }}</dd>
              <dt>Gender</dt><dd>{{ employee.gender || '—' }}</dd>
              <dt>Person with Disability</dt><dd>{{ employee.disabilityStatus || '—' }}</dd>
              <dt>Postal Code</dt><dd>{{ employee.postalCode || '—' }}</dd>
              <dt>Postal Town</dt><dd>{{ employee.postalTown || '—' }}</dd>
              <dt>Physical Address</dt><dd>{{ employee.physicalAddress || '—' }}</dd>
            </dl>
          </section>

          <section class="verification-section">
            <h3 class="verification-section-title">Extra Details</h3>
            <template v-if="submissionDetails.approved === 'false'">
              <div class="verification-form-grid">
                <label>Employment Type</label><input v-model="extraDetails.employmentType" type="text" class="verification-input" />
                <label>Education Level</label><input v-model="extraDetails.educationLevel" type="text" class="verification-input" />
                <label>Sub County</label><input v-model="extraDetails.subCounty" type="text" class="verification-input" />
                <label>Ward</label><input v-model="extraDetails.ward" type="text" class="verification-input" />
                <label>Marital Status</label><input v-model="extraDetails.maritalStatus" type="text" class="verification-input" />
                <label>Driving License</label><input v-model="extraDetails.drivingLicense" type="text" class="verification-input" />
                <label>Blood Group</label><input v-model="extraDetails.bloodGroup" type="text" class="verification-input" />
                <label>Spouse Name</label><input v-model="extraDetails.spouseName" type="text" class="verification-input" />
              </div>
            </template>
            <dl v-else class="verification-dl">
              <dt>Employment Type</dt><dd>{{ extraDetails.employmentType || '—' }}</dd>
              <dt>Education Level</dt><dd>{{ extraDetails.educationLevel || '—' }}</dd>
              <dt>Sub County</dt><dd>{{ extraDetails.subCounty || '—' }}</dd>
              <dt>Ward</dt><dd>{{ extraDetails.ward || '—' }}</dd>
              <dt>Marital Status</dt><dd>{{ extraDetails.maritalStatus || '—' }}</dd>
              <dt>Driving License</dt><dd>{{ extraDetails.drivingLicense || '—' }}</dd>
              <dt>Blood Group</dt><dd>{{ extraDetails.bloodGroup || '—' }}</dd>
              <dt>Spouse Name</dt><dd>{{ extraDetails.spouseName || '—' }}</dd>
            </dl>
          </section>

          <section class="verification-section">
            <h3 class="verification-section-title">Employment Details</h3>
            <template v-if="submissionDetails.approved === 'false'">
              <div class="verification-form-grid">
                <label>Department</label>
                <span v-if="employmentDetails.departmentName" class="verification-display-value">{{ employmentDetails.departmentName }}</span>
                <input v-else v-model="employmentDetails.department" type="text" class="verification-input" />
                <label>Section</label>
                <span v-if="employmentDetails.sectionName" class="verification-display-value">{{ employmentDetails.sectionName }}</span>
                <input v-else v-model="employmentDetails.section" type="text" class="verification-input" />
                <label>Unit</label>
                <span v-if="employmentDetails.unitName" class="verification-display-value">{{ employmentDetails.unitName }}</span>
                <input v-else v-model="employmentDetails.unit" type="text" class="verification-input" />
                <label>Sub Unit</label>
                <span v-if="employmentDetails.subUnitName" class="verification-display-value">{{ employmentDetails.subUnitName }}</span>
                <input v-else v-model="employmentDetails.subunit" type="text" class="verification-input" />
                <label>Designation</label><input v-model="employmentDetails.designation" type="text" class="verification-input" />
                <label>Employment Date</label><input v-model="employmentDetails.employmentDate" type="text" class="verification-input" />
                <label>Supervisor</label><input v-model="employmentDetails.reportingTo" type="text" class="verification-input" />
                <label>Net Salary</label><input v-model="employmentDetails.netSalary" type="text" class="verification-input" />
                <label>KRA PIN</label><input v-model="employmentDetails.kraPin" type="text" class="verification-input" />
                <label>SHIF Number</label><input v-model="employmentDetails.shifNumber" type="text" class="verification-input" />
                <label>NSSF Number</label><input v-model="employmentDetails.nssfNumber" type="text" class="verification-input" />
              </div>
            </template>
            <dl v-else class="verification-dl">
              <dt>Department</dt><dd>{{ employmentDetails.departmentName || employmentDetails.department || '—' }}</dd>
              <dt>Section</dt><dd>{{ employmentDetails.sectionName || employmentDetails.section || '—' }}</dd>
              <dt>Unit</dt><dd>{{ employmentDetails.unitName || employmentDetails.unit || '—' }}</dd>
              <dt>Sub Unit</dt><dd>{{ employmentDetails.subUnitName || employmentDetails.subunit || '—' }}</dd>
              <dt>Designation</dt><dd>{{ employmentDetails.designation || '—' }}</dd>
              <dt>Employment Date</dt><dd>{{ employmentDetails.employmentDate || '—' }}</dd>
              <dt>Supervisor</dt><dd>{{ employmentDetails.reportingTo || '—' }}</dd>
              <dt>Net Salary</dt><dd>{{ employmentDetails.netSalary || '—' }}</dd>
              <dt>KRA PIN</dt><dd>{{ employmentDetails.kraPin || '—' }}</dd>
              <dt>SHIF Number</dt><dd>{{ employmentDetails.shifNumber || '—' }}</dd>
              <dt>NSSF Number</dt><dd>{{ employmentDetails.nssfNumber || '—' }}</dd>
            </dl>
          </section>

          <section class="verification-section">
            <h3 class="verification-section-title">Bank Details</h3>
            <template v-if="submissionDetails.approved === 'false'">
              <div class="verification-form-grid">
                <label>Account Holder</label><input v-model="bankDetails.accountHolder" type="text" class="verification-input" />
                <label>Account Number</label><input v-model="bankDetails.accountNumber" type="text" class="verification-input" />
                <label>Bank Country</label><input v-model="bankDetails.bankCountry" type="text" class="verification-input" />
                <label>Bank Name</label><input v-model="bankDetails.bankName" type="text" class="verification-input" />
                <label>Branch Location</label><input v-model="bankDetails.branchLocation" type="text" class="verification-input" />
              </div>
            </template>
            <dl v-else class="verification-dl">
              <dt>Account Holder</dt><dd>{{ bankDetails.accountHolder || '—' }}</dd>
              <dt>Account Number</dt><dd>{{ bankDetails.accountNumber || '—' }}</dd>
              <dt>Bank Country</dt><dd>{{ bankDetails.bankCountry || '—' }}</dd>
              <dt>Bank Name</dt><dd>{{ bankDetails.bankName || '—' }}</dd>
              <dt>Branch Location</dt><dd>{{ bankDetails.branchLocation || '—' }}</dd>
            </dl>
          </section>

          <section class="verification-section" v-for="(contact, index) in emergencyContacts" :key="'ec-' + index">
            <h3 class="verification-section-title">Emergency Contact {{ emergencyContacts.length > 1 ? index + 1 : '' }}</h3>
            <template v-if="submissionDetails.approved === 'false'">
              <div class="verification-form-grid">
                <label>Name</label><input v-model="contact.name" type="text" class="verification-input" />
                <label>Email</label><input v-model="contact.email" type="text" class="verification-input" />
                <label>Phone</label><input v-model="contact.phone" type="text" class="verification-input" />
                <label>Relationship</label><input v-model="contact.relationship" type="text" class="verification-input" />
              </div>
            </template>
            <dl v-else class="verification-dl">
              <dt>Name</dt><dd>{{ contact.name || '—' }}</dd>
              <dt>Email</dt><dd>{{ contact.email || '—' }}</dd>
              <dt>Phone</dt><dd>{{ contact.phone || '—' }}</dd>
              <dt>Relationship</dt><dd>{{ contact.relationship || '—' }}</dd>
            </dl>
          </section>

          <section class="verification-section" v-for="(kin, index) in nextOfKin" :key="'nok-' + index">
            <h3 class="verification-section-title">Next of Kin {{ nextOfKin.length > 1 ? index + 1 : '' }}</h3>
            <template v-if="submissionDetails.approved === 'false'">
              <div class="verification-form-grid">
                <label>Name</label><input v-model="kin.name" type="text" class="verification-input" />
                <label>Email</label><input v-model="kin.email" type="text" class="verification-input" />
                <label>Phone</label><input v-model="kin.phoneNumber" type="text" class="verification-input" />
                <label>Relationship</label><input v-model="kin.relationship" type="text" class="verification-input" />
              </div>
            </template>
            <dl v-else class="verification-dl">
              <dt>Name</dt><dd>{{ kin.name || '—' }}</dd>
              <dt>Email</dt><dd>{{ kin.email || '—' }}</dd>
              <dt>Phone</dt><dd>{{ kin.phoneNumber || kin.phone || '—' }}</dd>
              <dt>Relationship</dt><dd>{{ kin.relationship || '—' }}</dd>
            </dl>
          </section>

          <section class="verification-section" v-if="documents && documents.length">
            <h3 class="verification-section-title">Documents</h3>
            <div class="verification-docs">
              <div v-for="doc in documents" :key="doc.id" class="verification-doc-row">
                <span class="verification-doc-name">{{ doc.name }}</span>
                <span class="verification-doc-meta">{{ doc.documentNumber || '—' }} · Valid till {{ doc.validTill || '—' }}</span>
                <a v-if="doc.filePath" :href="authUrl + doc.filePath" target="_blank" rel="noopener" class="verification-doc-link">View file</a>
              </div>
            </div>
          </section>

          <section class="verification-section">
            <h3 class="verification-section-title">Submission Details</h3>
            <dl class="verification-dl">
              <dt>Submitted By</dt><dd>{{ submissionDetails.createdBy || '—' }}</dd>
              <dt>Date Submitted</dt><dd>{{ submissionDetails.dateCreated || '—' }}</dd>
              <dt>Submitting Officer Role</dt><dd>{{ submissionDetails.submittingOfficerRole || '—' }}</dd>
            </dl>
          </section>

          <!-- Access profile: assign so user gets correct roles (Department HR, Employee, Governor, etc.) -->
          <section v-if="submissionDetails.approved === 'false'" class="verification-section verification-section-access">
            <h3 class="verification-section-title">Access profile</h3>
            <div v-if="loadingCategories" class="verification-loading-categories">Loading access profiles…</div>
            <div v-else class="verification-access-select-wrap">
              <label class="verification-label">Access profile <span class="text-danger">*</span></label>
              <select v-model="accessProfileCategoryId" class="verification-select" required>
                <option value="">Select profile (e.g. Department HR, Employee, Governor)</option>
                <option v-for="cat in availableCategoriesList" :key="cat.id" :value="cat.id">
                  {{ cat.categoryName }}
                  <span v-if="cat.usersLimit > 0"> ({{ cat.currentUsers || 0 }}/{{ cat.usersLimit }})</span>
                  <span v-else> (Unlimited)</span>
                </option>
              </select>
              <small v-if="accessProfileCategoryId && selectedAccessCategory" class="verification-access-roles">
                Roles: {{ (selectedAccessCategory.roles || []).join(', ') || 'None assigned' }}
              </small>
            </div>
          </section>

          <!-- Actions: only when still pending -->
          <footer v-if="submissionDetails.approved === 'false'" class="verification-actions">
            <button
              type="button"
              class="verification-btn verification-btn-success"
              :disabled="mustSelectAccessProfile"
              :title="mustSelectAccessProfile ? 'Select an Access profile above first' : ''"
              @click="onApproveClick"
            >
              <i class="bx bx-check-circle"></i> Approve Recruitment
            </button>
            <button type="button" class="verification-btn verification-btn-danger" @click="showDeclineModal = true">
              <i class="bx bx-x-circle"></i> Decline
            </button>
          </footer>
          <footer v-else class="verification-actions">
            <span class="verification-status-badge" :class="submissionDetails.approved === 'true' ? 'verification-status-approved' : 'verification-status-rejected'">
              {{ submissionDetails.approved === 'true' ? 'Approved' : 'Rejected' }}
            </span>
            <router-link :to="{ name: 'employee-approvals-new' }" class="verification-btn verification-btn-outline">Back to list</router-link>
          </footer>
        </div>
      </div>

      <!-- Confirm Approval overlay (pure Vue) -->
      <div v-if="showConfirmApprove" class="verification-overlay" @click.self="showConfirmApprove = false">
        <div class="verification-modal">
          <header class="verification-modal-header">
            <h4>Confirm Recruitment Approval</h4>
            <button type="button" class="verification-modal-close" aria-label="Close" @click="showConfirmApprove = false">×</button>
          </header>
          <div class="verification-modal-body">
            <template v-if="alert.info">
              <p class="verification-modal-text">Once you confirm, the employee will be added to the system with a user account (default password: <strong>1234</strong>, password expires one year from today). They should change their password on first login.</p>
              <p v-if="!accessProfileCategoryId && availableCategoriesList.length" class="verification-modal-warn">Please select an <strong>Access profile</strong> on the page (e.g. Department HR, Employee, Governor) before confirming.</p>
              <p v-else-if="!availableCategoriesList.length && !loadingCategories" class="verification-modal-warn">No access profiles found. Create them under System Management → Access profiles & roles, or approval will assign default &quot;Employee&quot;.</p>
              <button type="button" class="verification-btn verification-btn-success verification-btn-block" :disabled="!accessProfileCategoryId && availableCategoriesList.length > 0" @click="approveEmployment">
                Confirm Approval
              </button>
            </template>
            <template v-else-if="alert.success">
              <div class="verification-modal-result verification-modal-success">
                <i class="bx bx-check-circle"></i>
                <h4>Recruitment Approved Successfully</h4>
                <p>The recruitment record has been successfully approved. The employee is now officially added to the system.</p>
                <button type="button" class="verification-btn verification-btn-outline verification-btn-block" @click="closeConfirmAndGoBack">Close</button>
              </div>
            </template>
            <template v-else-if="alert.failed">
              <div class="verification-modal-result verification-modal-fail">
                <i class="bx bx-error-circle"></i>
                <h4>Approval could not be completed</h4>
                <p class="verification-error-message">{{ approvalErrorMessage || message }}</p>
                <p v-if="approvalErrorCode" class="verification-error-hint">You can correct the details above (e.g. National ID or email) and try again.</p>
                <button type="button" class="verification-btn verification-btn-outline verification-btn-block" @click="alert.failed = false; showConfirmApprove = false">Close and edit details</button>
              </div>
            </template>
          </div>
        </div>
      </div>

      <!-- Confirm Decline overlay (pure Vue) -->
      <div v-if="showDeclineModal" class="verification-overlay" @click.self="showDeclineModal = false">
        <div class="verification-modal verification-modal-danger">
          <header class="verification-modal-header">
            <h4>Confirm Application Decline</h4>
            <button type="button" class="verification-modal-close" aria-label="Close" @click="showDeclineModal = false">×</button>
          </header>
          <div class="verification-modal-body">
            <template v-if="alert.info">
              <p class="verification-modal-text">Provide a reason for declining this application (optional).</p>
              <label class="verification-label">Reason for decline</label>
              <textarea v-model="declineReason" class="verification-textarea" rows="3" placeholder="Enter reason..."></textarea>
              <button type="button" class="verification-btn verification-btn-danger verification-btn-block" @click="declinedEmployment">
                Confirm Decline
              </button>
            </template>
            <template v-else-if="alert.success">
              <div class="verification-modal-result verification-modal-fail">
                <i class="bx bx-x-circle"></i>
                <h4>Application Declined Successfully</h4>
                <p>The application has been declined.</p>
                <button type="button" class="verification-btn verification-btn-outline verification-btn-block" @click="closeDeclineAndGoBack">Close</button>
              </div>
            </template>
            <template v-else-if="alert.failed">
              <div class="verification-modal-result verification-modal-fail">
                <h4>Decline Failed</h4>
                <p>{{ message }}</p>
                <button type="button" class="verification-btn verification-btn-outline verification-btn-block" @click="alert.failed = false; showDeclineModal = false">Close</button>
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>
  </HrPageLayout>
</template>

<script>
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import { authUrl, execute, getRight } from "@/api";

export default {
  name: "EmployeeApprovalsNewVerification",
  components: { HrPageLayout },
  data() {
    return {
      message: "",
      alert: { success: false, info: true, failed: false },
      showConfirmApprove: false,
      showDeclineModal: false,
      declineReason: "",
      employee: {
        title: "", firstName: "", middleName: "", lastName: "", dob: "", email: "", phone: "",
        nationalId: "", gender: "", disabilityStatus: "", disabilityId: "", postalCode: "", postalTown: "", physicalAddress: ""
      },
      extraDetails: {
        employmentType: "", educationLevel: "", subCounty: "", ward: "", maritalStatus: "", drivingLicense: "", bloodGroup: "", spouseName: ""
      },
      employmentDetails: {
        department: "", section: "", unit: "", subunit: "", designation: "", employmentDate: "", reportingDate: "", reportingTo: "", netSalary: "", kraPin: "", shifNumber: "", nssfNumber: ""
      },
      bankDetails: {
        accountHolder: "", accountNumber: "", bankCountry: "", bankName: "", branchLocation: ""
      },
      documents: [],
      emergencyContacts: [],
      nextOfKin: [],
      submissionDetails: { approved: "false", createdBy: "", dateCreated: "", submittingOfficerRole: "" },
      categories: [],
      loadingCategories: false,
      accessProfileCategoryId: "",
      approvalErrorMessage: "",
      approvalErrorCode: ""
    };
  },
  computed: {
    availableCategoriesList() {
      return (this.categories || []).filter(
        c => c.status === "active" && (c.usersLimit === 0 || (c.currentUsers || 0) < c.usersLimit)
      );
    },
    mustSelectAccessProfile() {
      return this.availableCategoriesList.length > 0 && !this.accessProfileCategoryId;
    },
    selectedAccessCategory() {
      if (!this.accessProfileCategoryId) return null;
      return (this.categories || []).find(c => String(c.id) === String(this.accessProfileCategoryId));
    },
    authUrl() {
      return authUrl;
    },
    pageTitle() {
      const first = this.employee.firstName || "";
      const last = this.employee.lastName || "";
      return first || last ? `${first} ${last}`.trim() || "Recruitment verification" : "Recruitment verification";
    },
    breadcrumbItems() {
      return [
        { label: "Home", route: "/dashboard" },
        { label: "Employee", route: null },
        { label: "Employee Approvals", route: "employee-approvals-new" },
        { label: this.pageTitle }
      ];
    }
  },
  watch: {
    showConfirmApprove(v) {
      if (v) this.alert = { success: false, info: true, failed: false };
    },
    showDeclineModal(v) {
      if (v) this.alert = { success: false, info: true, failed: false };
    }
  },
  mounted() {
    this.getEmployeeDataById();
    this.getCategories();
  },
  methods: {
    getRight(type) {
      return getRight(type);
    },
    gotTo(routeName) {
      this.$router.push({ name: routeName });
    },
    getEmployeeDataById() {
      const id = sessionStorage.getItem("employeeDataId");
      if (!id) {
        this.message = "No recruitment selected.";
        return;
      }
      const data = new FormData();
      data.append("function", "getEmployeeDataById");
      data.append("employeeDataId", id);
      execute(data, authUrl)
        .then((res) => {
          if (res.data.success && res.data.data && res.data.data.employeeData) {
            const d = res.data.data.employeeData;
            this.employee = d.employeeData || this.employee;
            this.extraDetails = d.extraDetails || this.extraDetails;
            this.employmentDetails = { kraPin: '', shifNumber: '', nssfNumber: '', ...(d.employmentDetails || {}) };
            this.bankDetails = d.bankDetails || this.bankDetails;
            // Legacy: show SHIF/NSSF under Employment Details if they were stored in bankDetails
            if (this.bankDetails && (this.bankDetails.shifNumber || this.bankDetails.nssfNumber)) {
              if (this.bankDetails.shifNumber) this.employmentDetails.shifNumber = this.bankDetails.shifNumber;
              if (this.bankDetails.nssfNumber) this.employmentDetails.nssfNumber = this.bankDetails.nssfNumber;
            }
            this.documents = Array.isArray(d.documents) ? d.documents : [];
            this.emergencyContacts = Array.isArray(d.emergencyContacts) ? d.emergencyContacts : [];
            this.nextOfKin = Array.isArray(d.nextOfKin) ? d.nextOfKin : [];
            this.submissionDetails = res.data.data.submissionDetails || this.submissionDetails;
          } else {
            this.message = res.data.message || "Failed to load data.";
          }
        })
        .catch((e) => {
          this.message = e.message || "Failed to load data.";
        });
    },
    getCategories() {
      this.loadingCategories = true;
      const data = new FormData();
      data.append("function", "getCategories");
      execute(data, authUrl)
        .then((res) => {
          this.loadingCategories = false;
          const cats = (res.data && res.data.data && res.data.data.categories) || res.data?.data?.categories || res.data?.categories;
          if (res.data && (res.data.success || cats)) {
            this.categories = Array.isArray(cats) ? cats : [];
            const available = (this.categories || []).filter(
              c => c.status === "active" && (c.usersLimit === 0 || (c.currentUsers || 0) < c.usersLimit)
            );
            if (!this.accessProfileCategoryId && available.length) {
              const employeeCat = available.find(
                c => (c.categoryName || "").toLowerCase() === "employee"
              );
              this.accessProfileCategoryId = employeeCat ? employeeCat.id : available[0].id;
            }
          }
        })
        .catch(() => {
          this.loadingCategories = false;
        });
    },
    onApproveClick() {
      if (this.mustSelectAccessProfile) {
        this.message = "Please select an Access profile (e.g. Department HR, Employee, Governor) above before approving.";
        return;
      }
      this.showConfirmApprove = true;
    },
    approveEmployment() {
      this.alert.info = true;
      this.alert.success = false;
      this.alert.failed = false;
      this.approvalErrorMessage = '';
      this.approvalErrorCode = '';
      const data = new FormData();
      data.append("function", "approveEmployment");
      data.append("employeeDataId", sessionStorage.getItem("employeeDataId"));
      if (this.accessProfileCategoryId) {
        data.append("categoryId", this.accessProfileCategoryId);
        const cat = this.selectedAccessCategory;
        if (cat && cat.categoryName) data.append("category", cat.categoryName);
      }
      const overrides = {
        employeeData: { ...this.employee },
        employmentDetails: { ...this.employmentDetails },
        extraDetails: { ...this.extraDetails },
        bankDetails: { ...this.bankDetails },
        emergencyContacts: this.emergencyContacts.map(c => ({ ...c })),
        nextOfKin: this.nextOfKin.map(k => ({ ...k, phone: k.phone || k.phoneNumber }))
      };
      data.append("approvalOverrides", JSON.stringify(overrides));
      execute(data, authUrl)
        .then((res) => {
          const resData = res && res.data;
          const msg = (resData && resData.message) ? String(resData.message) : "";
          const ok = resData && (
            resData.success === true ||
            (res.status === 200 && msg && (msg.toLowerCase().includes("approved") || msg.toLowerCase().includes("success")))
          );
          if (ok) {
            this.alert.info = false;
            this.alert.success = true;
            this.submissionDetails = { ...this.submissionDetails, approved: "true" };
          } else {
            this.alert.info = false;
            this.alert.failed = true;
            this.approvalErrorMessage = (resData && resData.message) || "Approval failed.";
            this.approvalErrorCode = (resData && resData.code) || null;
            this.message = this.approvalErrorMessage;
          }
        })
        .catch((e) => {
          const resp = e && e.response;
          const resData = resp && resp.data;
          const msg = (resData && resData.message) ? String(resData.message) : "";
          const ok = resp && resp.status === 200 && resData && (
            resData.success === true ||
            (msg && (msg.toLowerCase().includes("approved") || msg.toLowerCase().includes("success")))
          );
          if (ok) {
            this.alert.info = false;
            this.alert.success = true;
            this.submissionDetails = { ...this.submissionDetails, approved: "true" };
          } else {
            this.alert.info = false;
            this.alert.failed = true;
            this.approvalErrorMessage = (resData && resData.message) || e.message || "Approval failed.";
            this.approvalErrorCode = (resData && resData.code) || null;
            this.message = this.approvalErrorMessage;
          }
        });
    },
    declinedEmployment() {
      this.alert.info = true;
      this.alert.success = false;
      this.alert.failed = false;
      const data = new FormData();
      data.append("function", "declinedEmployment");
      data.append("employeeDataId", sessionStorage.getItem("employeeDataId"));
      if (this.declineReason) data.append("reason", this.declineReason);
      execute(data, authUrl)
        .then((res) => {
          if (res.data.success) {
            this.alert.info = false;
            this.alert.success = true;
            this.submissionDetails = { ...this.submissionDetails, approved: "REJECTED" };
          } else {
            this.alert.info = false;
            this.alert.failed = true;
            this.message = res.data.message || "Decline failed.";
          }
        })
        .catch((e) => {
          this.alert.info = false;
          this.alert.failed = true;
          this.message = e.message || "Decline failed.";
        });
    },
    closeConfirmAndGoBack() {
      this.showConfirmApprove = false;
      this.alert.success = false;
      this.gotTo("employee-approvals-new");
    },
    closeDeclineAndGoBack() {
      this.showDeclineModal = false;
      this.alert.success = false;
      this.declineReason = "";
      this.gotTo("employee-approvals-new");
    }
  }
};
</script>

<style scoped>
@import "@/assets/css/hr-page-shared.css";

.verification-page {
  max-width: 900px;
  margin: 0 auto;
  padding: 0 1rem 2rem;
}

.verification-toast {
  position: fixed;
  bottom: 1.5rem;
  right: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  cursor: pointer;
}
.verification-toast-success { background: #d1fae5; color: #065f46; }
.verification-toast-error { background: #fee2e2; color: #991b1b; }
.verification-toast-close { background: none; border: none; font-size: 1.25rem; cursor: pointer; opacity: 0.8; }

.verification-card {
  background: #fff;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  overflow: hidden;
}

.verification-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.75rem;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #e2e8f0;
  background: #f8fafc;
}
.verification-card-title {
  margin: 0;
  font-size: 1.125rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.02em;
}
.verification-back-link {
  color: #2563eb;
  text-decoration: none;
  font-size: 0.9rem;
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
}
.verification-back-link:hover { text-decoration: underline; }

.verification-card-body { padding: 1.5rem; }

.verification-section {
  margin-bottom: 1.75rem;
}
.verification-section-access { background: #f0fdf4; padding: 1rem; border-radius: 8px; border: 1px solid #bbf7d0; }
.verification-access-help { margin: 0 0 0.75rem; font-size: 0.875rem; color: #166534; }
.verification-loading-categories { color: #64748b; font-size: 0.9rem; }
.verification-access-select-wrap { max-width: 400px; }
.verification-label { display: block; margin-bottom: 0.25rem; font-weight: 600; font-size: 0.9rem; }
.verification-select { width: 100%; padding: 0.5rem 0.75rem; border: 1px solid #e2e8f0; border-radius: 6px; font-size: 0.95rem; }
.verification-access-roles { display: block; margin-top: 0.5rem; font-size: 0.8rem; color: #64748b; }
.verification-modal-warn { margin-top: 0.5rem; padding: 0.5rem; background: #fef3c7; color: #92400e; border-radius: 6px; font-size: 0.875rem; }

.verification-section-title {
  margin: 0 0 0.75rem;
  padding: 0.5rem 0.75rem;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  background: #e0f2fe;
  color: #0369a1;
  border-radius: 6px;
}
.verification-edit-hint {
  margin: 0 0 0.75rem;
  font-size: 0.875rem;
  color: #64748b;
}
.verification-form-grid {
  display: grid;
  grid-template-columns: 140px 1fr;
  gap: 0.5rem 1rem;
  align-items: center;
  max-width: 560px;
}
.verification-form-grid label {
  font-size: 0.875rem;
  font-weight: 500;
  color: #334155;
}
.verification-input {
  padding: 0.4rem 0.6rem;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 0.9rem;
}
.verification-input:focus {
  outline: none;
  border-color: #2563eb;
  box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.2);
}
.verification-display-value {
  padding: 0.4rem 0.6rem;
  font-size: 0.9rem;
  color: #334155;
}
.verification-error-message {
  margin: 0 0 0.5rem;
  font-weight: 500;
  color: #991b1b;
}
.verification-error-hint {
  margin: 0 0 1rem;
  font-size: 0.875rem;
  color: #64748b;
}

.verification-dl {
  display: grid;
  grid-template-columns: 180px 1fr;
  gap: 0.4rem 1.5rem;
  margin: 0;
  font-size: 0.9rem;
}
.verification-dl dt {
  margin: 0;
  color: #64748b;
  font-weight: 500;
}
.verification-dl dd { margin: 0; }

.verification-docs { display: flex; flex-direction: column; gap: 0.5rem; }
.verification-doc-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem 1rem;
  padding: 0.5rem 0;
  border-bottom: 1px solid #f1f5f9;
}
.verification-doc-name { font-weight: 500; }
.verification-doc-meta { color: #64748b; font-size: 0.85rem; }
.verification-doc-link { color: #2563eb; text-decoration: none; }
.verification-doc-link:hover { text-decoration: underline; }

.verification-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  padding-top: 1.5rem;
  margin-top: 1.5rem;
  border-top: 1px solid #e2e8f0;
}
.verification-status-badge {
  display: inline-block;
  padding: 0.35rem 0.75rem;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 600;
}
.verification-status-approved { background: #d1fae5; color: #065f46; }
.verification-status-rejected { background: #fee2e2; color: #991b1b; }

.verification-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.35rem;
  padding: 0.5rem 1rem;
  font-size: 0.9rem;
  font-weight: 600;
  border-radius: 8px;
  border: 1px solid transparent;
  cursor: pointer;
  text-decoration: none;
  transition: background 0.2s, color 0.2s;
}
.verification-btn-block { width: 100%; }
.verification-btn-success { background: #10b981; color: #fff; border-color: #10b981; }
.verification-btn-success:hover { background: #059669; }
.verification-btn-success:disabled { opacity: 0.6; cursor: not-allowed; background: #9ca3af; border-color: #9ca3af; }
.verification-btn-danger { background: #ef4444; color: #fff; border-color: #ef4444; }
.verification-btn-danger:hover { background: #dc2626; }
.verification-btn-outline { background: transparent; color: #475569; border-color: #cbd5e1; }
.verification-btn-outline:hover { background: #f1f5f9; }

/* Overlay & modal (pure Vue, no Bootstrap) */
.verification-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 1rem;
}
.verification-modal {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
  max-width: 440px;
  width: 100%;
  max-height: 90vh;
  overflow: auto;
}
.verification-modal-danger .verification-modal-header { background: #fef2f2; color: #991b1b; }
.verification-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid #e2e8f0;
  background: #f0f9ff;
}
.verification-modal-header h4 { margin: 0; font-size: 1rem; text-transform: uppercase; letter-spacing: 0.03em; }
.verification-modal-close {
  background: none;
  border: none;
  font-size: 1.5rem;
  line-height: 1;
  cursor: pointer;
  opacity: 0.7;
  padding: 0 0.25rem;
}
.verification-modal-close:hover { opacity: 1; }
.verification-modal-body { padding: 1.25rem; }
.verification-modal-text { margin: 0 0 1rem; font-size: 0.9rem; color: #475569; }
.verification-label { display: block; margin-bottom: 0.35rem; font-size: 0.875rem; font-weight: 600; color: #374151; }
.verification-textarea {
  width: 100%;
  padding: 0.5rem 0.75rem;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  font-size: 0.9rem;
  margin-bottom: 1rem;
  resize: vertical;
}
.verification-modal-result {
  text-align: center;
  padding: 0.5rem 0;
}
.verification-modal-result i { font-size: 3rem; display: block; margin-bottom: 0.75rem; }
.verification-modal-result h4 { margin: 0 0 0.5rem; font-size: 1rem; }
.verification-modal-result p { margin: 0 0 1rem; font-size: 0.9rem; color: #64748b; }
.verification-modal-success i { color: #10b981; }
.verification-modal-fail i { color: #ef4444; }
</style>
